<script setup>
import { ref, computed, onMounted, onBeforeUnmount, defineProps, defineEmits, nextTick } from 'vue'
import axios from 'axios'

const props = defineProps({
  selectedTable: String,
  gridMode: Boolean,
  gridTabs: Array,
  gridLayout: String,
  autoFocus: {
    type: Boolean,
    default: false
  }
})

const emit = defineEmits(['table-select', 'table-deleted', 'open-structure', 'open-data', 'open-popup'])

const tables = ref([])
const loading = ref(false)
const searchQuery = ref('')
const showDeleteConfirm = ref('')
const showOptionsMenu = ref('')
const searchInput = ref(null)
const selectedIndex = ref(0) // For arrow key navigation

// Fuzzy search function
const fuzzyMatch = (text, pattern) => {
  if (!pattern) return true
  
  const textLower = text.toLowerCase()
  const patternLower = pattern.toLowerCase()
  
  // Direct substring match (highest priority)
  if (textLower.includes(patternLower)) {
    return { score: 100, match: true }
  }
  
  // Fuzzy match - check if all pattern characters appear in order
  let textIndex = 0
  let patternIndex = 0
  let score = 0
  
  while (textIndex < textLower.length && patternIndex < patternLower.length) {
    if (textLower[textIndex] === patternLower[patternIndex]) {
      score += 1
      patternIndex++
    }
    textIndex++
  }
  
  // All pattern characters found in order
  if (patternIndex === patternLower.length) {
    // Calculate score based on how compact the match is
    const compactness = score / textLower.length
    return { score: 50 + (compactness * 50), match: true }
  }
  
  return { score: 0, match: false }
}

const filteredTables = computed(() => {
  if (!searchQuery.value) {
    selectedIndex.value = 0
    return tables.value
  }
  
  const results = tables.value
    .map(table => {
      const match = fuzzyMatch(table.name, searchQuery.value)
      return { ...table, searchScore: match.score, searchMatch: match.match }
    })
    .filter(table => table.searchMatch)
    .sort((a, b) => b.searchScore - a.searchScore)
  
  // Reset selected index when results change
  selectedIndex.value = 0
  
  return results
})

const getGridPosition = (tableName) => {
  if (!props.gridMode || !props.gridTabs) return null
  const index = props.gridTabs.findIndex(tab => tab.tableName === tableName)
  return index !== -1 ? index + 1 : null
}

const isInGrid = (tableName) => {
  return getGridPosition(tableName) !== null
}

const getGridPositionLabel = (position) => {
  const labels = ['①', '②', '③', '④']
  return labels[position - 1] || position.toString()
}

const getMaxGridSlots = () => {
  if (props.gridLayout === '2x1' || props.gridLayout === '1x2') {
    return 2
  }
  return 4
}

const loadTables = async () => {
  loading.value = true
  try {
    const response = await axios.get('/api/database-tables')
    tables.value = response.data.tables
  } catch (error) {
    console.error('Error loading tables:', error)
  } finally {
    loading.value = false
  }
}

const deleteTable = async (tableName) => {
  try {
    await axios.delete(`/api/database-tables/${tableName}`)
    await loadTables()
    emit('table-deleted', tableName)
  } catch (error) {
    console.error('Error deleting table:', error)
  }
  showDeleteConfirm.value = ''
}

const confirmDelete = (tableName) => {
  showDeleteConfirm.value = tableName
  showOptionsMenu.value = ''
}

const toggleOptionsMenu = (tableName) => {
  showOptionsMenu.value = showOptionsMenu.value === tableName ? '' : tableName
}

const handleMouseEnter = (index) => {
  selectedIndex.value = index
}

// Close options menu when clicking outside
const handleClickOutside = (event) => {
  if (!event.target.closest('.relative')) {
    showOptionsMenu.value = ''
  }
}

// Handle keyboard events for search input
const handleSearchKeydown = (event) => {
  const maxIndex = filteredTables.value.length - 1
  
  switch (event.key) {
    case 'ArrowDown':
      event.preventDefault()
      if (filteredTables.value.length > 0) {
        selectedIndex.value = selectedIndex.value < maxIndex ? selectedIndex.value + 1 : 0
      }
      break
      
    case 'ArrowUp':
      event.preventDefault()
      if (filteredTables.value.length > 0) {
        selectedIndex.value = selectedIndex.value > 0 ? selectedIndex.value - 1 : maxIndex
      }
      break
      
    case 'Enter':
      event.preventDefault()
      if (filteredTables.value.length > 0) {
        // Select the currently highlighted table
        const selectedTable = filteredTables.value[selectedIndex.value]
        if (selectedTable) {
          emit('table-select', selectedTable.name)
        }
      }
      break
      
    case 'Escape':
      event.preventDefault()
      searchQuery.value = ''
      selectedIndex.value = 0
      break
  }
}

onMounted(() => {
  document.addEventListener('click', handleClickOutside)
  if (props.autoFocus) {
    nextTick(() => {
      searchInput.value.focus()
    })
  }
})

onBeforeUnmount(() => {
  document.removeEventListener('click', handleClickOutside)
})

onMounted(() => {
  loadTables()
})
</script>

  <template>
   <div class="p-2 bg-gray-700 text-gray-50">
    <!-- Grid Mode Status -->
    <div v-if="gridMode" class="mb-2 p-2 bg-emerald-50  border border-emerald-200 rounded-md">
      <div class="flex items-center justify-between mb-1.5">
        <div class="flex items-center">
          <i class="fas fa-th-large text-emerald-600 mr-1.5 text-xs"></i>
          <span class="text-xs font-medium text-emerald-900">Grid Active</span>
          <span class="ml-1.5 px-1.5 py-0.5 bg-emerald-200 text-emerald-800 text-xs rounded">{{ gridLayout }}</span>
        </div>
        <span class="text-xs text-emerald-600">{{ gridTabs?.length || 0 }}/{{ getMaxGridSlots() }}</span>
      </div>
      <div v-if="gridTabs?.length > 0" class="grid grid-cols-2 gap-0.5 text-xs">
        <div 
          v-for="(tab, index) in gridTabs" 
          :key="tab.id"
          class="flex items-center p-0.5 bg-white rounded border"
        >
          <span class="inline-flex items-center justify-center w-3 h-3 bg-emerald-500 text-white text-xs font-bold rounded-full mr-1 flex-shrink-0">
            {{ getGridPositionLabel(index + 1) }}
          </span>
          <span class="truncate text-emerald-900 text-xs">{{ tab.tableName }}</span>
        </div>
        <div 
          v-for="emptySlot in (getMaxGridSlots() - (gridTabs?.length || 0))" 
          :key="`empty-${emptySlot}`"
          class="flex items-center p-0.5 bg-gray-100 rounded border border-dashed border-gray-300"
        >
          <span class="inline-flex items-center justify-center w-3 h-3 bg-gray-300 text-gray-500 text-xs rounded-full mr-1 flex-shrink-0">
            {{ getGridPositionLabel((gridTabs?.length || 0) + emptySlot) }}
          </span>
          <span class="text-gray-500 text-xs">Empty</span>
        </div>
      </div>
    </div>

    <div class="mb-2">
      <div class="relative">
        <input
          v-model="searchQuery"
          type="text"
          placeholder="Search tables... (↑↓ to navigate, Enter to select, Esc to clear)"
          class="w-full pl-8 pr-3 py-1.5 text-xs border border-gray-500 bg-gray-600 text-gray-50 placeholder-gray-400 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          ref="searchInput"
          @keydown="handleSearchKeydown"
        >
        <i class="fas fa-search absolute left-2.5 top-2 text-gray-400 text-xs"></i>
      </div>
    </div>

    <div v-if="loading" class="text-center py-6">
      <i class="fas fa-spinner fa-spin text-xl text-gray-400"></i>
      <p class="text-gray-300 mt-2 text-sm">Loading tables...</p>
    </div>

    <div v-else class="space-y-0">
      <div
        v-for="(table, index) in filteredTables"
        :key="table.name"
        class="group relative"
      >
        <div
          class="px-2 py-1 rounded cursor-pointer transition-colors"
          :class="{
            'bg-slate-600 border-l-2 border-slate-400': selectedTable === table.name && !gridMode,
            'bg-emerald-600 border-l-2 border-emerald-400': isInGrid(table.name),
            'bg-slate-500 border-l-2 border-slate-300': index === selectedIndex && searchQuery && filteredTables.length > 0 && selectedTable !== table.name && !isInGrid(table.name),
            'hover:bg-gray-600': selectedTable !== table.name && !isInGrid(table.name) && !(index === selectedIndex && searchQuery && filteredTables.length > 0)
          }"
          @click="emit('table-select', table.name)"
          @mouseenter="handleMouseEnter(index)"
        >
          <div class="flex items-center justify-between">
            <div class="flex-1 min-w-0 flex items-center">
              <i class="fas fa-table text-gray-300 mr-1.5 text-xs flex-shrink-0"></i>
              <span class="font-normal text-gray-50 text-[14px]">{{ table.name }}</span>
              <span class="ml-2 text-xs flex-shrink-0" :class="isInGrid(table.name) ? 'text-emerald-300' : 'text-gray-300'">
                {{ table.row_count }}r • {{ table.columns.length }}c
              </span>
              <!-- Grid Position Indicator -->
              <span 
                v-if="isInGrid(table.name)" 
                class="ml-1.5 inline-flex items-center justify-center w-4 h-4 bg-emerald-500 text-white text-xs font-bold rounded-full flex-shrink-0"
                :title="`Grid position ${getGridPosition(table.name)}`"
              >
                {{ getGridPositionLabel(getGridPosition(table.name)) }}
              </span>
              <!-- Enter to select indicator -->
              <span 
                v-if="index === selectedIndex && searchQuery && filteredTables.length > 0 && selectedTable !== table.name && !isInGrid(table.name)"
                class="ml-2 text-xs text-blue-300 font-medium"
              >
                ↵ Enter
              </span>
            </div>
            
            <div class="relative flex items-center gap-1">
              <!-- Quick Popup Data Shortcut -->
              <button
                @click.stop="emit('open-popup', table.name, 'data')"
                class="opacity-0 group-hover:opacity-100 transition-opacity p-0.5 hover:bg-blue-600 rounded"
                title="Quick popup data"
              >
                <i class="fas fa-external-link-alt text-blue-400 text-xs"></i>
              </button>
              
              <button
                @click.stop="toggleOptionsMenu(table.name)"
                class="opacity-0 group-hover:opacity-100 transition-opacity p-0.5 hover:bg-gray-500 rounded"
                title="Table options"
              >
                <i class="fas fa-ellipsis-v text-gray-300 text-xs"></i>
              </button>

              <!-- Options Menu -->
              <div
                v-if="showOptionsMenu === table.name"
                class="absolute right-0 top-8 w-40 bg-white border border-gray-200 rounded-md shadow-lg z-10"
                @click.stop
              >
                <div class="py-1">
                  <button
                    @click="emit('open-structure', table.name); showOptionsMenu = ''"
                    class="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                  >
                    <i class="fas fa-cog mr-2"></i>
                    View Structure
                  </button>
                  <button
                    @click="emit('open-data', table.name); showOptionsMenu = ''"
                    class="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                  >
                    <i class="fas fa-table mr-2"></i>
                    View Data
                  </button>
                  <button
                    @click="emit('open-popup', table.name, 'data'); showOptionsMenu = ''"
                    class="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                  >
                    <i class="fas fa-external-link-alt mr-2"></i>
                    Popup Data
                  </button>
                  <button
                    @click="emit('open-popup', table.name, 'structure'); showOptionsMenu = ''"
                    class="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                  >
                    <i class="fas fa-external-link-alt mr-2"></i>
                    Popup Structure
                  </button>
                  <div class="border-t border-gray-200 my-1"></div>
                  <button
                    @click="confirmDelete(table.name)"
                    class="block w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-red-50"
                  >
                    <i class="fas fa-trash mr-2"></i>
                    Delete Table
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Delete Confirmation -->
        <div v-if="showDeleteConfirm === table.name" 
             class="mt-3 p-3 bg-red-50 border border-red-200 rounded">
          <p class="text-sm text-red-800 mb-2">
            Are you sure you want to delete table "{{ table.name }}"? This action cannot be undone.
          </p>
          <div class="flex space-x-2">
            <button
              @click="deleteTable(table.name)"
              class="bg-red-600 hover:bg-red-700 text-white px-3 py-1 rounded text-sm"
            >
              Delete
            </button>
            <button
              @click="showDeleteConfirm = ''"
              class="bg-gray-300 hover:bg-gray-400 text-gray-700 px-3 py-1 rounded text-sm"
            >
              Cancel
            </button>
          </div>
        </div>
      </div>

      <div v-if="filteredTables.length === 0 && !loading" class="text-center py-6">
        <i class="fas fa-table text-3xl text-gray-400 mb-2"></i>
        <p class="text-gray-300 text-sm">No tables found</p>
      </div>
    </div>
  </div>
</template> 